/*
  rima atelier · "Personalise My Abaya" builder
  Drives snippets/rima-personalise.liquid. Pure rules (one word, palette
  parsing) live in rima-personalise-core.js, which is unit tested.

  Flow: five steps, one open at a time, each unlocked by confirming the one
  before (Modify reopens a done step). The photo previews the stitching live.
  Nothing reaches the order until "Add to My Abaya": then the four hidden
  property inputs are filled and enabled. Remove disables them again.

  Vanilla ES, progressive enhancement: without js the button does nothing and
  no property is sent, so the order is never half personalised.
  Guide: docs/personalisation.md
*/
(function () {
  'use strict';

  // Dancing Script is a monoline script, the kind embroidery machines and hand
  // stitchers actually use for names; Pinyon Script (hairline contrast) looked printed.
  // Sacramento (monoline cursive) is the base for Mirage: thin, even lines are
  // what the stitch trick below needs.
  var SCRIPT_FONT = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&family=Sacramento&display=swap';
  // the photos are 1000 x 1250; positions are percentages of that
  var VB_W = 1000;
  var VB_H = 1250;
  // a script reads smaller than capitals at the same size
  var STYLE_SCALE = { Classic: 1.22, Modern: 1, Mirage: 1.32 };
  var STYLE_CLASS = { Classic: 'rima-pz-font--classic', Modern: 'rima-pz-font--modern', Mirage: 'rima-pz-font--stitch' };

  /*
    Mirage (the stitched style), the trick: dash the OUTLINE of a thin monoline script with square
    ends and a stroke wider than the letter. Every dash then sits perpendicular to
    the letter's line, so each one reads as a single stitch crossing it, and the
    stitches follow every curve. All geometry scales with the font size (em
    fractions tuned against the founder's chain and bean stitch references).
    Other styles get a solid fill plus a hairline outline (satin stitch body).
  */
  function applyThread(textEl, style, hex, fontSize) {
    if (style === 'Mirage') {
      textEl.setAttribute('fill', 'none');
      textEl.setAttribute('stroke', hex);
      textEl.setAttribute('stroke-width', (fontSize * 0.054).toFixed(2));
      textEl.setAttribute('stroke-dasharray', (fontSize * 0.033).toFixed(2) + ' ' + (fontSize * 0.0135).toFixed(2));
      textEl.setAttribute('stroke-linecap', 'butt');
    } else {
      textEl.setAttribute('fill', hex);
      textEl.setAttribute('stroke', hex);
      textEl.setAttribute('stroke-width', (fontSize * 0.022).toFixed(2));
      textEl.removeAttribute('stroke-dasharray');
      textEl.setAttribute('stroke-linecap', 'round');
    }
    textEl.setAttribute('stroke-linejoin', 'round');
  }
  var ORDER = ['name', 'style', 'thread', 'position', 'confirm'];
  var Core = window.RimaPersonaliseCore;

  function loadScriptFont() {
    if (document.querySelector('link[data-pz-font]')) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = SCRIPT_FONT;
    link.setAttribute('data-pz-font', '');
    document.head.appendChild(link);
  }

  /** Roving radio group: click, arrow keys, aria-checked. */
  function radioGroup(container, selector, onPick) {
    function items() { return Array.prototype.slice.call(container.querySelectorAll(selector)); }
    function pick(el, focus) {
      items().forEach(function (i) {
        var on = i === el;
        i.classList.toggle('is-selected', on);
        i.setAttribute('aria-checked', String(on));
        i.tabIndex = on ? 0 : -1;
      });
      if (focus) el.focus();
      onPick(el);
    }
    container.addEventListener('click', function (e) {
      var el = e.target.closest(selector);
      if (el && container.contains(el)) pick(el);
    });
    container.addEventListener('keydown', function (e) {
      var list = items();
      var i = list.indexOf(document.activeElement);
      if (i < 0) return;
      var next = e.key === 'ArrowRight' || e.key === 'ArrowDown';
      var prev = e.key === 'ArrowLeft' || e.key === 'ArrowUp';
      if (!next && !prev) return;
      e.preventDefault();
      pick(list[(i + (next ? 1 : -1) + list.length) % list.length], true);
    });
    var sel = items().filter(function (i) { return i.classList.contains('is-selected'); })[0] || items()[0];
    items().forEach(function (i) { i.tabIndex = i === sel ? 0 : -1; });
    return { pick: pick, items: items };
  }

  function init(root) {
    if (!Core) return;
    var config;
    try { config = JSON.parse(root.querySelector('[data-pz-config]').textContent); } catch (e) { return; }

    var dialog = root.querySelector('[data-pz-dialog]');
    var opener = root.querySelector('[data-pz-open]');
    var saved = root.querySelector('[data-pz-saved]');
    var img = root.querySelector('[data-pz-img]');
    var stitch = root.querySelector('[data-pz-stitch]');
    var caption = root.querySelector('[data-pz-caption]');
    var nameInput = root.querySelector('[data-pz-name]');
    var countEl = root.querySelector('[data-pz-count]');
    var errorEl = root.querySelector('[data-pz-error]');
    var customInput = root.querySelector('[data-pz-custom-input]');
    var customLabel = root.querySelector('.rima-pz__custom');
    var props = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-pz-prop]'), function (p) { props[p.getAttribute('data-pz-prop')] = p; });

    var max = Number(config.max) || Core.DEFAULT_MAX;
    var threads = Core.parseThreads(config.threads);
    // the preselected thread is a setting ("Default thread"), matched by name
    var want = String(config.defaultThread || '').trim().toLowerCase();
    var defaultIdx = Math.max(0, threads.findIndex(function (t) { return t.name.toLowerCase() === want; }));
    var positions = config.positions || [];
    var lastFocus = null;

    var state = {
      name: '',
      // the tile marked is-selected in the snippet is the default style
      style: (root.querySelector('[data-pz-style].is-selected') || {}).getAttribute ? root.querySelector('[data-pz-style].is-selected').getAttribute('data-pz-style') : 'Mirage',
      thread: threads[defaultIdx] || { custom: true, hex: '#FFFFFF' },
      // "default": true in the POSITIONS config, else the first
      position: positions.filter(function (p) { return p.default; })[0] || positions[0],
      done: {},
      active: 'name',
      saved: false
    };

    /* ---------- build the palette and the positions from config ---------- */
    var threadBox = root.querySelector('[data-pz-threads]');
    threads.forEach(function (t, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'rima-pz__thread' + (i === defaultIdx ? ' is-selected' : '');
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', String(i === defaultIdx));
      b.setAttribute('data-pz-thread', t.name);
      b.innerHTML = '<span class="rima-pz__thread-dot" aria-hidden="true"></span><span class="rima-pz__thread-name"></span>';
      b.querySelector('.rima-pz__thread-dot').style.background = t.hex;
      b.querySelector('.rima-pz__thread-name').textContent = t.name;
      threadBox.appendChild(b);
    });
    var threadGroup = radioGroup(threadBox, '[data-pz-thread]', function (el) {
      var name = el.getAttribute('data-pz-thread');
      state.thread = threads.filter(function (t) { return t.name === name; })[0];
      customLabel.classList.remove('is-selected');
      render();
    });
    customInput.addEventListener('input', function () {
      state.thread = { custom: true, hex: customInput.value };
      threadGroup.items().forEach(function (i) { i.classList.remove('is-selected'); i.setAttribute('aria-checked', 'false'); });
      customLabel.classList.add('is-selected');
      customLabel.style.setProperty('--pz-custom', customInput.value);
      render();
    });

    var posBox = root.querySelector('[data-pz-positions]');
    positions.forEach(function (p, i) {
      var b = document.createElement('button');
      b.type = 'button';
      var isDefault = p === state.position;
      b.className = 'rima-pz__position' + (isDefault ? ' is-selected' : '');
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', String(isDefault));
      b.setAttribute('data-pz-position', p.id);
      b.innerHTML = '<span class="rima-pz__check" aria-hidden="true"></span><span></span>';
      b.lastChild.textContent = p.label;
      posBox.appendChild(b);
      // warm the other photo so switching position never flashes
      if (config.images[p.image]) new Image().src = config.images[p.image];
    });
    radioGroup(posBox, '[data-pz-position]', function (el) {
      var id = el.getAttribute('data-pz-position');
      state.position = positions.filter(function (p) { return p.id === id; })[0];
      render();
    });

    radioGroup(root.querySelector('.rima-pz__tiles'), '[data-pz-style]', function (el) {
      state.style = el.getAttribute('data-pz-style');
      render();
    });

    /* ---------- preview ---------- */
    function threadHex() { return Core.normaliseHex(state.thread && state.thread.hex) || '#C9A35B'; }

    function render() {
      var p = state.position;
      if (p) {
        var src = config.images[p.image];
        if (src && img.getAttribute('src') !== src) img.setAttribute('src', src);
        // this abaya's photo calibration (custom.personalisation_offset), in %
        var off = (config.offsets && config.offsets[p.image]) || [0, 0];
        var x = ((p.x + (Number(off[0]) || 0)) / 100) * VB_W;
        var y = ((p.y + (Number(off[1]) || 0)) / 100) * VB_H;
        stitch.setAttribute('x', x.toFixed(1));
        stitch.setAttribute('y', y.toFixed(1));
        stitch.setAttribute('transform', 'rotate(' + (p.rotate || 0) + ' ' + x.toFixed(1) + ' ' + y.toFixed(1) + ')');
        stitch.setAttribute('font-size', ((p.size || 4) / 100 * VB_W * (STYLE_SCALE[state.style] || 1)).toFixed(1));
        caption.textContent = p.label;
      }
      var shown = state.name || 'Your Name';
      stitch.textContent = shown;
      stitch.classList.toggle('is-placeholder', !state.name);
      Object.keys(STYLE_CLASS).forEach(function (k) { stitch.classList.toggle(STYLE_CLASS[k], state.style === k); });
      applyThread(stitch, state.style, threadHex(), parseFloat(stitch.getAttribute('font-size')) || 40);
      // the Mirage tile shows the stitch in ink, like the other two samples
      // (the thread can be white, which would vanish on a white tile)
      var stitchSample = root.querySelector('[data-pz-stitch-sample]');
      if (stitchSample) applyThread(stitchSample, 'Mirage', '#14110F', 54);
      Array.prototype.forEach.call(root.querySelectorAll('[data-pz-sample]'), function (el) {
        el.textContent = state.name || 'Rima';
      });
      if (typeof syncLens === 'function') syncLens();
    }

    /* ---------- magnifier ---------- */
    var lens = root.querySelector('[data-pz-lens]');
    var lensInner = root.querySelector('[data-pz-lens-inner]');
    var stage = root.querySelector('[data-pz-stage]');
    var preview = root.querySelector('.rima-pz__preview');
    var ZOOM = 2.4;
    var lensOn = false;
    var lensFrame = 0;
    var lensPoint = null;

    // a fresh copy of the photo and the stitching, so the lens shows exactly
    // what the client chose. svg is cloned, so the name stays sharp when scaled.
    function syncLens() {
      if (!lensOn) return;
      lensInner.innerHTML = '';
      lensInner.appendChild(img.cloneNode(false));
      lensInner.appendChild(stage.querySelector('svg').cloneNode(true));
    }

    function placeLens() {
      lensFrame = 0;
      if (!lensPoint) return;
      var s = stage.getBoundingClientRect();
      var f = preview.getBoundingClientRect();
      var size = lens.offsetWidth;
      // the point on the photo being magnified, clamped to the photo
      var x = Math.min(Math.max(lensPoint.x - s.left, 0), s.width);
      var y = Math.min(Math.max(lensPoint.y - s.top, 0), s.height);
      // on touch, float the lens above the finger so the finger never hides it
      var lift = lensPoint.touch ? size * 0.75 : 0;
      lens.style.left = (s.left - f.left + x - size / 2) + 'px';
      lens.style.top = (s.top - f.top + y - size / 2 - lift) + 'px';
      lensInner.style.width = s.width + 'px';
      lensInner.style.height = s.height + 'px';
      lensInner.style.transform = 'translate(' + (size / 2 - x * ZOOM) + 'px,' + (size / 2 - y * ZOOM) + 'px) scale(' + ZOOM + ')';
    }

    function showLens(e) {
      lensPoint = { x: e.clientX, y: e.clientY, touch: e.pointerType !== 'mouse' };
      if (!lensOn) { lensOn = true; syncLens(); lens.classList.add('is-on'); }
      if (!lensFrame) lensFrame = requestAnimationFrame(placeLens);
    }

    function hideLens() {
      lensOn = false;
      lensPoint = null;
      lens.classList.remove('is-on');
    }

    stage.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'mouse' || lensOn) showLens(e);
    });
    stage.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') hideLens(); });
    // touch and hold: a short press opens the lens, then drag to move it
    var holdTimer = 0;
    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      clearTimeout(holdTimer);
      var start = e;
      holdTimer = setTimeout(function () { showLens(start); }, 180);
    });
    ['pointerup', 'pointercancel'].forEach(function (t) {
      stage.addEventListener(t, function (e) {
        if (e.pointerType === 'mouse') return;
        clearTimeout(holdTimer);
        hideLens();
      });
    });
    stage.addEventListener('contextmenu', function (e) { if (lensOn) e.preventDefault(); });

    /* ---------- steps ---------- */
    function stepEl(id) { return root.querySelector('[data-pz-step="' + id + '"]'); }
    function summaryFor(id) {
      if (id === 'name') return state.name;
      if (id === 'style') return state.style;
      if (id === 'thread') return Core.threadLabel(state.thread);
      if (id === 'position') return state.position ? state.position.label : '';
      return '';
    }
    function firstOpen() {
      for (var i = 0; i < ORDER.length; i++) if (!state.done[ORDER[i]]) return i;
      return ORDER.length - 1;
    }

    function paintSteps() {
      var limit = firstOpen();
      ORDER.forEach(function (id, i) {
        var el = stepEl(id);
        var toggle = el.querySelector('.rima-pz__step-toggle');
        var body = el.querySelector('[data-pz-body]');
        var open = id === state.active;
        body.hidden = !open;
        el.classList.toggle('is-open', open);
        el.classList.toggle('is-done', !!state.done[id] && !open);
        toggle.setAttribute('aria-expanded', String(open));
        toggle.disabled = i > limit;
        el.querySelector('[data-pz-summary]').textContent = state.done[id] && !open ? summaryFor(id) : '';
      });
      if (state.active === 'confirm') paintReview();
    }

    function goto(id, focus) {
      state.active = id;
      paintSteps();
      var el = stepEl(id);
      // keep the open step in view inside the scrolling panel, never jump the page
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      if (focus === false) return;
      var target = id === 'name' ? nameInput : el.querySelector('[data-pz-body] .is-selected, [data-pz-body] button, [data-pz-body] input');
      if (target) target.focus({ preventScroll: true });
    }

    function validateName(showError) {
      var r = Core.cleanName(nameInput.value, max);
      if (showError || !r.ok) {
        errorEl.textContent = showError ? r.error : '';
        errorEl.hidden = !(showError && r.error);
      }
      nameInput.setAttribute('aria-invalid', String(!r.ok && showError));
      return r;
    }

    function confirmStep(id) {
      if (id === 'name') {
        var r = validateName(true);
        if (!r.ok) { nameInput.focus(); return; }
        state.name = r.value;
        nameInput.value = r.value;
      }
      state.done[id] = true;
      var next = ORDER[ORDER.indexOf(id) + 1] || 'confirm';
      render();
      goto(next);
    }

    function paintReview() {
      var dl = root.querySelector('[data-pz-review]');
      dl.innerHTML = '';
      [['Name or Initials', state.name], ['Style', state.style], ['Thread Colour', Core.threadLabel(state.thread)],
        ['Position', state.position ? state.position.label : '']].forEach(function (row) {
        var dt = document.createElement('dt');
        var dd = document.createElement('dd');
        dt.textContent = row[0];
        dd.textContent = row[1];
        dl.appendChild(dt);
        dl.appendChild(dd);
      });
    }

    /* ---------- save, remove ---------- */
    /* ---------- the fee ----------
       Saving gives the abaya line a hidden _pz key. rima-personalise-cart.js is
       the ONE owner of the fee line: straight after the abaya lands in the cart
       it adds "Abaya Personalisation" with the same key and quantity, and keeps
       the two in step afterwards. (Two owners raced and could double the fee.)
       Buy It Now goes straight to checkout and cannot carry the fee, so it is
       hidden while the abaya is personalised. */
    var form = props.name.form || document.getElementById(props.name.getAttribute('form'));
    var formWrap = form ? (form.closest('product-form') || form.parentElement) : null;
    var binNote = root.querySelector('[data-pz-bin-note]');

    function setBuyItNow(hidden) {
      var payment = formWrap ? formWrap.querySelector('.shopify-payment-button') : null;
      if (payment) payment.classList.toggle('rima-pz-bin-hidden', hidden);
      if (binNote) binNote.hidden = !hidden || !payment;
    }

    function newKey() {
      return 'pz' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    }

    function save() {
      if (!Core.cleanName(state.name, max).ok) { goto('name'); return; }
      props.key.value = newKey();
      props.name.value = state.name;
      props.style.value = state.style;
      props.thread.value = Core.threadLabel(state.thread);
      props.position.value = state.position.label;
      Object.keys(props).forEach(function (k) { props[k].disabled = false; });
      state.saved = true;
      root.querySelector('[data-pz-saved-name]').textContent = state.name;
      root.querySelector('[data-pz-saved-meta]').textContent =
        '· ' + Core.threadLabel(state.thread) + ', ' + state.style + ', ' + state.position.label;
      saved.hidden = false;
      opener.hidden = true;
      if (config.fee) setBuyItNow(true);
      close(saved.querySelector('[data-pz-edit]'));
    }

    function remove() {
      Object.keys(props).forEach(function (k) { props[k].value = ''; props[k].disabled = true; });
      state.saved = false;
      saved.hidden = true;
      opener.hidden = false;
      setBuyItNow(false);
      opener.focus();
    }

    /* ---------- open, close ---------- */
    function open() {
      loadScriptFont();
      lastFocus = document.activeElement;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.documentElement.classList.add('rima-pz-lock');
      render();
      goto(state.saved ? 'confirm' : state.active);
    }

    function close(returnTo) {
      hideLens();
      if (dialog.open) {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      }
      document.documentElement.classList.remove('rima-pz-lock');
      var back = returnTo || lastFocus || opener;
      if (back && back.focus) back.focus({ preventScroll: true });
    }

    /* ---------- wiring ---------- */
    opener.addEventListener('click', open);
    root.querySelector('[data-pz-edit]').addEventListener('click', open);
    root.querySelector('[data-pz-remove]').addEventListener('click', remove);
    root.querySelector('[data-pz-close]').addEventListener('click', function () { close(); });
    root.querySelector('[data-pz-save]').addEventListener('click', save);
    dialog.addEventListener('cancel', function (e) { e.preventDefault(); close(); }); // Escape
    dialog.addEventListener('click', function (e) { if (e.target === dialog) close(); }); // backdrop

    Array.prototype.forEach.call(root.querySelectorAll('[data-pz-goto]'), function (b) {
      b.addEventListener('click', function () { goto(b.getAttribute('data-pz-goto')); });
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-pz-confirm]'), function (b) {
      b.addEventListener('click', function () { confirmStep(b.getAttribute('data-pz-confirm')); });
    });

    nameInput.addEventListener('input', function () {
      var r = Core.cleanName(nameInput.value, max);
      countEl.textContent = String(Array.from(nameInput.value.trim().replace(/[ً-ٰٟ]/g, '')).length);
      state.name = r.ok ? r.value : nameInput.value.trim().split(/\s+/)[0] || '';
      if (state.done.name && !r.ok) state.done.name = false;
      errorEl.hidden = true;
      render();
    });
    nameInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); confirmStep('name'); }
    });

    render();
    paintSteps();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-rima-pz]'), init);
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
